import * as React from 'react'

import {
  PageMeta,
  HowToUse,
  BehindScenes,
  Callout,
  Flow,
  KVTable,
} from '@/components/docs/primitives'
import { H3, P, UL } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function DocJobs() {
  return (
    <>
      <PageMeta
        title="Created Assets"
        summary={`Every ${brand.productName} ad generation — video or image — lands here. Watch it run, inspect what was sent to the AI, save the output, and iterate.`}
      />

      <HowToUse>
        <H3>The queue</H3>
        <P>
          Every job carries a status label that tells you where it is:
        </P>
        <UL>
          <li>
            <strong>Queued</strong> — waiting for a render slot. Your Radiance video is in
            line and will start shortly.
          </li>
          <li>
            <strong>Processing</strong> — actively rendering. Seedance is working on it.
            Videos typically take a few minutes.
          </li>
          <li>
            <strong>Completed</strong> — ready to view, save, and use.
          </li>
          <li>
            <strong>Failed</strong> — something went wrong. Usually a moderation flag or a
            bad reference image. The job detail will tell you what happened in plain English.
          </li>
          <li>
            <strong>Cancelled</strong> — you stopped it before it finished.
          </li>
        </UL>
        <P>
          Filter the queue by status, aspect ratio, output type (video or image), or search
          by prompt text to find a specific job.
        </P>

        <H3>Opening a job</H3>
        <P>
          Click any job to open its detail view. Four things matter here:
        </P>
        <UL>
          <li>
            <strong>The output player</strong> — watch the video or view the image.
          </li>
          <li>
            <strong>The original prompt</strong> — the sentence you typed in the wizard.
          </li>
          <li>
            <strong>The built prompt</strong> — the full text the AI actually received, after
            all style templates, reference tags, and constraints were applied. This is almost
            always longer and more specific than what you typed.
          </li>
          <li>
            <strong>Generation settings</strong> — the video style, aspect ratio, duration,
            reference images, seed, and any overrides that were active when the job was
            submitted.
          </li>
        </UL>
        <Callout variant="tip">
          The built prompt is your single best debugging tool. If a Radiance ad rendered in
          the wrong style, open the job and read the built prompt — you will usually see that
          the UGC bias block was not applied because the video style was set to Cinematic, or
          that the reference tag for your hero image did not land at the start of the prompt.
          Fix those, re-run, and compare.
        </Callout>

        <H3>Re-running with a tweak</H3>
        <P>
          Copy the seed from a completed job, change one thing — the prompt wording, the video
          style, the reference images — and submit a new job. Using the same seed keeps the
          underlying motion and composition similar, so you can see the effect of your single
          change cleanly without other variables shifting.
        </P>

        <H3>Cancelling</H3>
        <P>
          Only queued jobs can be cancelled cleanly. Once a job moves to Processing, Seedance
          or OmniHuman is already rendering — cancelling sends a stop signal but the render
          slot has already been used.
        </P>

        <H3>Saving outputs</H3>
        <P>
          A completed job's video or image is hosted temporarily. Click Save to Library to
          copy it into your Asset Library permanently, where it becomes available as a
          reference for future renders — a completed Novera Body Oil ad, for example, can
          become a style reference for the next one.
        </P>

        <H3>Deleting</H3>
        <P>
          Deleting removes the job record. If you saved the output to your Asset Library
          before deleting, that copy stays. If you did not, the output is gone — so save
          anything you want to keep before you delete.
        </P>
      </HowToUse>

      <BehindScenes>
        <H3>The render lifecycle</H3>
        <Flow
          steps={[
            'You hit Generate somewhere in the app',
            'A job is created with status Queued',
            'A render worker picks it up and flips the status to Processing',
            'The AI provider is called with your prompt and any reference images or videos',
            'When the output is ready, the URL is saved and status flips to Completed',
            'If something goes wrong, status flips to Failed with a plain-English message explaining what happened',
          ]}
        />

        <H3>Why a job can fail</H3>
        <UL>
          <li>
            The AI's moderation filter flagged something in the prompt or a reference image.
            This is most common with real-person photos or prompts that describe physical
            transformations in medical-sounding language.
          </li>
          <li>
            The provider was briefly rate-limited or timed out. These usually resolve on their
            own — the system retries automatically before marking the job as failed.
          </li>
          <li>
            A reference image was too large, corrupt, or not publicly accessible at render
            time.
          </li>
          <li>
            The prompt was empty or the generation settings were incomplete.
          </li>
        </UL>
        <Callout variant="warn">
          If you see repeated failures on the same prompt, it is almost always a moderation
          flag. Rephrase the prompt — avoid medical claims, skin-transformation language, or
          before-and-after framing. If you are using a real person's photo in Composite or
          Avatar, swap it for a stock-style portrait.
        </Callout>

        <H3>Watching progress</H3>
        <P>
          The Jobs page polls for updates every five seconds while a job is running. You do
          not need to refresh manually — the status badge and output player update on their own.
        </P>

        <H3>What is in a job record</H3>
        <KVTable
          rows={[
            {
              key: 'Prompt',
              value: 'The sentence you typed — for example, "Novera Body Oil on a marble ledge, golden hour, woman\'s hand lifting the bottle"',
            },
            {
              key: 'Built prompt',
              value:
                'The full text the AI received after style templates, reference tags, and constraints were applied. Often two to three times longer than your original prompt.',
            },
            {
              key: 'Video style',
              value: 'UGC, Cinematic, Lifestyle, or Hype Retail — the style profile that shaped the cinematic prefix and negative prompt',
            },
            {
              key: 'Reference images',
              value:
                'Up to three images bound as reference inputs — for example, the Radiance hero shot and a lifestyle mood board',
            },
            {
              key: 'Seed',
              value:
                'The random number that anchors the motion and composition. Copy a seed from a job you liked and paste it into a new generation to reproduce a similar feel.',
            },
            {
              key: 'Negative prompt',
              value:
                'What the AI was told to avoid — typically "avoid jitter, avoid bent limbs, avoid temporal flicker, avoid watermarks"',
            },
            {
              key: 'Cost',
              value: 'Approximate billed cost for this render',
            },
            {
              key: 'Render time',
              value: 'How long the AI provider took from start to output',
            },
          ]}
        />

        <Callout variant="info">
          Built prompts are saved permanently. Treat the Jobs page as your prompt journal —
          scrolling through past jobs is how you learn which phrasings and style combinations
          produced which looks for your product line.
        </Callout>
      </BehindScenes>
    </>
  )
}
